// Pure, framework-free helpers for computing per-item, discount- and
// prior-payment-aware net dues on a pickup bill.
//
// Extracted out of PickupModal.tsx (rather than duplicated) so that:
//  - the live pickup-selection screen (dueForSelectedItems/amountDue), and
//  - the itemized partial-pickup receipt (PickupReceiptModal, built from
//    PickupModal's handlePickupSubmit)
// always reconcile to the exact same numbers, and so this math is
// unit-testable in isolation without rendering the whole modal.
//
// Deliberately typed against minimal, structural (duck-typed) shapes rather
// than PickupModal's own IPickupCartItem/IPickupCartBill so this module has
// no dependency on that component - any object with these fields (which
// IPickupCartItem/IPickupCartBill are supersets of) satisfies them.

export interface INetDueCartItem {
  itemId: string;
  selected: boolean;
  qty: number;
  unitPrice: number;
  alterationItems: { unitPrice?: number | null }[];
  upcharges: { subUpcharge: { price: number } }[];
  spots: { subSpot: { price?: number | null } }[];
  // Pieces of this line already handed over, and pieces selected to leave in
  // this transaction. Absent for callers that predate partial-quantity pickup,
  // where a line was collected whole - `releasedFractionOf` then treats a
  // selected line as fully released and an unselected one as not released.
  pickedQty?: number;
  selectedQty?: number;
}

// Matches the gross-total components the backend's pro-rata discount split
// sums per item (itemPrice*qty + alterationTotal + its own upcharges + its
// own spot charges) - see BillServiceImpl.updateBillDiscount /
// getItemDiscountShares on the backend.
export const itemTotal = (item: INetDueCartItem): number =>
  item.qty * item.unitPrice +
  item.alterationItems.reduce((acc, curr) => acc + (curr?.unitPrice ?? 0), 0) +
  item.upcharges.reduce((acc, curr) => acc + curr.subUpcharge.price, 0) +
  item.spots.reduce((acc, curr) => acc + (curr.subSpot.price ?? 0), 0);

// Splits `total` across `weights` proportionally, rounded to the cent, with
// any leftover cent(s) from rounding absorbed by the last weight so the
// shares always sum EXACTLY to `total` - mirrors the backend's pro-rata
// discount apportionment (BillServiceImpl.getItemDiscountShares) so the FE's
// own per-item split stays consistent with it.
export const apportion = (total: number, weights: number[]): number[] => {
  const sumWeights = weights.reduce((acc, w) => acc + w, 0);
  if (!weights.length) return [];
  if (sumWeights <= 0 || total === 0) return weights.map(() => 0);

  const raw = weights.map(
    (w) => Math.round(((total * w) / sumWeights) * 100) / 100
  );
  const roundedSum = raw.reduce((acc, v) => acc + v, 0);
  const diff = Math.round((total - roundedSum) * 100) / 100;
  if (diff !== 0) {
    raw[raw.length - 1] = Math.round((raw[raw.length - 1] + diff) * 100) / 100;
  }
  return raw;
};

export interface INetDueResult {
  itemId: string;
  selected: boolean;
  netDue: number;
}

// Full per-item breakdown behind computeItemNetDues (see below) - exposes
// the intermediate grossTotal/discountShare/paidAmount components as well
// as the final netDue, so consumers that need a selection-scoped SUBTOTAL,
// DISCOUNT or PAID figure (not just the net due) can sum the same
// already-computed per-item numbers instead of re-deriving them from
// scratch. There is deliberately only ONE place (this function) that does
// the gross/discount-share/paid-share apportionment - computeItemNetDues is
// just a thin projection of it down to {itemId, selected, netDue}.
export interface INetDueBreakdown {
  itemId: string;
  selected: boolean;
  // This item's pre-discount gross total (itemTotal(item)).
  grossTotal: number;
  // This item's pro-rata share of the bill's effective discount.
  discountShare: number;
  // This item's own item-scoped paid amount plus its pro-rata share of any
  // unattributed (legacy, non item-scoped) bill-level payment.
  paidAmount: number;
  // grossTotal - discountShare - paidAmount, floored at 0.
  netDue: number;
}

// Per-item breakdown for every item on a bill: item.grossTotal minus its
// pro-rata discountShare (mirroring the backend's split) minus whatever's
// already been paid specifically for that item.
//
// Legacy bill-level (non item-scoped) payments - e.g. a deposit collected
// at check-in - aren't attributed to any single item by the per-item paid
// field, so any part of `billPaidAmount` not already accounted for by the
// sum of item-scoped paid amounts ("unattributed paid") is apportioned
// across all items the same way the discount is. This keeps the maths
// consistent (all-items-selected always nets back to exactly
// grossBillTotal - discount - billPaidAmount, so full-bill pickup with no
// partial selection has zero regression) while still giving a fair,
// item-scoped due for genuine partial selections.
export const computeItemBreakdown = (
  items: INetDueCartItem[],
  effectiveDiscount: number,
  billPaidAmount: number,
  itemPaidAmountFor: (itemId: string) => number
): INetDueBreakdown[] => {
  const grossTotals = items.map((ci) => itemTotal(ci));
  const discountShares = apportion(effectiveDiscount, grossTotals);
  const itemPaidAmounts = items.map((ci) => itemPaidAmountFor(ci.itemId));
  const totalItemPaid = itemPaidAmounts.reduce((acc, v) => acc + v, 0);
  const unattributedPaid = Math.max(0, billPaidAmount - totalItemPaid);
  // Weight the unattributed split by each item's own REMAINING balance
  // (post-discount, minus its own item-scoped paid amount) - not raw
  // grossTotals. An item already fully covered by its own item-scoped
  // payment must get zero weight here, so it can't "steal" a share of the
  // unattributed payment that a still-owing item actually needs. Using
  // grossTotals let an over-covered item soak up credit past its own price
  // (wasted, since netDue floors at 0) while a genuinely-unpaid item was
  // left under-credited - so a fully-paid bill's items could sum to a false
  // positive balance even though nothing was actually still owed overall.
  const remainingBeforeUnattributed = items.map((ci, idx) =>
    Math.max(0, grossTotals[idx] - discountShares[idx] - itemPaidAmounts[idx])
  );
  const unattributedShares = apportion(
    unattributedPaid,
    remainingBeforeUnattributed
  );

  return items.map((ci, idx) => {
    const paidAmount = itemPaidAmounts[idx] + unattributedShares[idx];
    return {
      itemId: ci.itemId,
      selected: ci.selected,
      grossTotal: grossTotals[idx],
      discountShare: discountShares[idx],
      paidAmount,
      netDue: Math.max(0, grossTotals[idx] - discountShares[idx] - paidAmount),
    };
  });
};

// Thin projection of computeItemBreakdown down to just {itemId, selected,
// netDue} - kept as its own export since it's the shape most call sites
// (dueForSelectedItems, the itemized receipt) actually want.
export const computeItemNetDues = (
  items: INetDueCartItem[],
  effectiveDiscount: number,
  billPaidAmount: number,
  itemPaidAmountFor: (itemId: string) => number
): INetDueResult[] =>
  computeItemBreakdown(
    items,
    effectiveDiscount,
    billPaidAmount,
    itemPaidAmountFor
  ).map(({ itemId, selected, netDue }) => ({ itemId, selected, netDue }));

// The share of a line that is (or is about to be) in the customer's hands:
// pieces already collected plus pieces selected to leave now, over the pieces
// on the line. 1 for a fully-collected line, 0 for one still untouched.
export const releasedFractionOf = (item: INetDueCartItem): number => {
  const pieces = item.qty ?? 0;
  if (pieces <= 0) return item.selected ? 1 : 0;
  // Pre-partial-quantity callers only carry `selected`.
  if (item.pickedQty === undefined && item.selectedQty === undefined) {
    return item.selected ? 1 : 0;
  }
  const released = (item.pickedQty ?? 0) + (item.selectedQty ?? 0);
  return Math.min(1, Math.max(0, released / pieces));
};

export interface IReleasedDueResult {
  itemId: string;
  // pieces released / pieces on the line
  releasedFraction: number;
  // What the released pieces are worth: their share of the line's
  // gross-minus-discount value.
  releasedValue: number;
  // Still owed for those pieces, once everything already paid against this
  // line is applied to them.
  releasedDue: number;
  // Still owed for the pieces that stay in storage.
  remainingDue: number;
}

/**
 * What a partially-collected line still owes.
 *
 * Charging for part of a line is NOT the same as pro-rating its net due.
 * `computeItemBreakdown`'s netDue already has the line's prior payments taken
 * out, so scaling THAT by the selected pieces would apply those payments a
 * second time - a 4-piece $44 line with $11 already paid for the piece the
 * customer collected would ask $24.75 for the last three instead of $33, and
 * the bill could never be settled from this screen.
 *
 * The pieces are priced first, then payments are applied against them:
 *   released value = (pieces released / pieces on line) x (gross - discount)
 *   released due   = released value - everything paid so far on this line
 * with any surplus payment credited to the pieces still in storage. That is
 * the same basis the backend's own pickup release guard uses, so what this
 * screen asks for is exactly what the server requires before the garments may
 * leave.
 */
export const computeReleasedDues = (
  items: INetDueCartItem[],
  effectiveDiscount: number,
  billPaidAmount: number,
  itemPaidAmountFor: (itemId: string) => number
): IReleasedDueResult[] =>
  computeItemBreakdown(
    items,
    effectiveDiscount,
    billPaidAmount,
    itemPaidAmountFor
  ).map((breakdown, idx) => {
    const releasedFraction = releasedFractionOf(items[idx]);
    const linePrice = breakdown.grossTotal - breakdown.discountShare;
    const releasedValue = round2(linePrice * releasedFraction);
    const releasedDue = Math.max(
      0,
      round2(releasedValue - breakdown.paidAmount)
    );
    const remainingDue = Math.max(
      0,
      round2(linePrice - breakdown.paidAmount - releasedDue)
    );

    return {
      itemId: breakdown.itemId,
      releasedFraction,
      releasedValue,
      releasedDue,
      remainingDue,
    };
  });

const round2 = (value: number): number => Math.round(value * 100) / 100;
